import React from 'react'
import useSWRInfinite from 'swr/infinite'

import BodyWidget, { BodyWidgetProps } from '../../atoms/BodyWidget'
import LoadingSpinner from '../../atoms/LoadingSpinner'
import Infinite from '../../organisms/ChatWindow/Timeline/Infinite'

import useStyles from './styles'

export type APITaskData =
  | WithTokenPagination<APITask[]>
  | WithIndexPagination<APITask[]>

type TaskWidgetProps = Omit<BodyWidgetProps, 'title' | 'children'> & {
  title: string
  getKey: (pageIndex: number, previousPageData: APITaskData) => string
  format?: (task: APITask[]) => (APITask & Record<string, any>)[]
  rowRenderer: (props: {
    tasks: (APITask & Record<string, any>)[]
    after?: string | number
  }) => React.ReactNode[]
  noTasksText?: string
}

export const PAGE_SIZE = 25

const TaskWidget: React.FC<TaskWidgetProps> = ({
  title,
  getKey,
  format,
  rowRenderer,
  noTasksText = 'No tasks',
  ...props
}) => {
  const containerRef = React.useRef()
  const { data, size, setSize } = useSWRInfinite<APITaskData>(getKey)

  const tasks = React.useMemo(() => (data ? [].concat(...data) : []), [data])
  const isLoadingInitialData = React.useMemo(() => !data, [data])
  const isLoadingMore = React.useMemo(
    () =>
      isLoadingInitialData ||
      (size > 0 && data && typeof data[size - 1] === 'undefined'),
    [isLoadingInitialData, size, data]
  )
  const after = React.useMemo(() => {
    if (tasks.length === 0) {
      return
    }

    const { next, page, per_page, total } = tasks[tasks.length - 1]?.pagination
    if (next || next === null) {
      return next as string
    } else {
      if (page + 1 < Math.ceil(total / per_page)) {
        return (page + 1) as number
      }
      return null
    }
  }, [tasks])
  const flatTasks = React.useMemo<APITask[]>(
    () => tasks.flatMap(x => x.data),
    [tasks]
  )
  const empty = React.useMemo(
    () => isLoadingInitialData && flatTasks.length === 0,
    [isLoadingInitialData, flatTasks]
  )
  const formatted = React.useMemo(() => {
    if (!format) {
      return flatTasks
    }

    return format(flatTasks)
  }, [format, flatTasks])

  const classes = useStyles({ empty })

  const emptyDiv = <div className={classes.emptyTitle}>{noTasksText}</div>

  // Not ideal but needed here, this function/component has to return an array to work with Infinite
  const children = rowRenderer({ tasks: formatted, after })

  return (
    <BodyWidget title={title} {...props}>
      <div ref={containerRef} className={classes.root}>
        {isLoadingInitialData ? (
          <LoadingSpinner className={classes.loading} />
        ) : empty ? (
          emptyDiv
        ) : (
          // @ts-ignore
          <Infinite
            containerRef={containerRef}
            PAGE_SIZE={PAGE_SIZE}
            loadAtTop={false}
            TRIGGER_HEIGHT={50}
            setSize={setSize}
            size={size}
            isLoadingMore={isLoadingMore}
            after={after}
            isSWR
          >
            {children}
          </Infinite>
        )}

        {!isLoadingInitialData && !Boolean(formatted.length) && emptyDiv}
      </div>

      {!isLoadingInitialData && !empty && <div className={classes.bottomPad} />}
    </BodyWidget>
  )
}

export default TaskWidget
